import React from "react";
import AppleSignin from "react-apple-signin-auth";
import { useTranslation } from "react-i18next";
import usePostData from "Hooks/usePostData";
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from "config/appleAuth";
import SocialAuthButton from "./SocialAuthButton";

const AppleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.05 12.04c-.03-2.68 2.19-3.97 2.29-4.03-1.25-1.83-3.2-2.08-3.89-2.1-1.65-.17-3.23.97-4.07.97-.86 0-2.14-.95-3.52-.92-1.81.03-3.48 1.05-4.41 2.67-1.88 3.26-.48 8.08 1.35 10.73.89 1.3 1.95 2.76 3.33 2.71 1.34-.05 1.85-.86 3.48-.86 1.62 0 2.08.86 3.5.84 1.45-.02 2.36-1.32 3.24-2.63 1.02-1.51 1.44-2.97 1.47-3.05-.03-.02-2.82-1.08-2.85-4.32zm-2.67-7.93c.74-.9 1.23-2.14 1.09-3.38-1.06.04-2.34.7-3.1 1.59-.68.79-1.28 2.06-1.12 3.27 1.18.09 2.39-.6 3.13-1.48z" />
  </svg>
);

const AppleAuth = ({ onSuccess, onError }) => {
  const { postData } = usePostData();
  const { t } = useTranslation();

  const handleSuccess = async (res) => {
    // Apple returns { authorization: { id_token, code }, user? }.
    // The `user` object is ONLY sent on the first sign-in — persist it now
    // or it's gone forever.
    const identityToken = res?.authorization?.id_token;
    if (!identityToken) {
      onError?.("Apple sign-in returned no token");
      return;
    }

    const response = await postData("auth/apple", {
      identityToken,
      firstName: res?.user?.name?.firstName || null,
      lastName: res?.user?.name?.lastName || null,
      source: "web",
    });

    if (response?.success) onSuccess(response);
    else onError?.(response?.message || "Apple login failed");
  };

  return (
    <AppleSignin
      authOptions={{
        clientId: APPLE_CLIENT_ID,
        scope: "email name",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      }}
      uiType="dark"
      onSuccess={handleSuccess}
      onError={(err) => {
        // Apple fires onError when the user dismisses the popup too —
        // don't surface those as failures.
        if (err?.error === "popup_closed_by_user") return;
        onError?.("Apple login failed");
      }}
      render={({ onClick, disabled }) => (
        <SocialAuthButton
          icon={<AppleIcon />}
          label={t("auth.login.continue_apple")}
          onClick={onClick}
          disabled={disabled}
          ariaLabel="Continue with Apple"
        />
      )}
    />
  );
};

export default AppleAuth;
